// src/pages/MatchSetup/useSetupState.js
//
// Polls /api/scorer/matches/:matchId/setup-state on an adaptive interval.
//   - 3 s  when it's the OPPONENT's turn (you want to see their pick fast)
//   - 8 s  when it's YOUR turn (you don't need polling — you're picking)
//   - 10 s when both squads are full or no toss winner yet
//   - paused when the page is hidden (Page Visibility API)
//
// `myMemberId` lets the hook detect "is it my turn?". Pass null for
// admin/scorer view → always uses the fast interval.

import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../utils/api';

const FAST_MS = 3000;
const SLOW_MS = 8000;
const IDLE_MS = 10000;

export default function useSetupState(matchId, myMemberId) {
  const [state,   setState]   = useState(null);
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const inFlight = useRef(false);
  const stateRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await api.get(`/scorer/matches/${matchId}/setup-state`);
      setState(data);
      stateRef.current = data;
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'load failed');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [matchId]);

  function computeInterval(s) {
    if (!s || !s.captaincy?.squad_selection_winner_id) return IDLE_MS;
    const target = (s.match?.players_per_side || 11) - 1;
    const aPicked = s.squad.filter(m => m.side === 'A' && !m.is_captain && !m.is_saanjha).length;
    const bPicked = s.squad.filter(m => m.side === 'B' && !m.is_captain && !m.is_saanjha).length;
    if (aPicked >= target && bPicked >= target) return IDLE_MS;
    const swSide = s.squad.find(m => m.member_id === s.captaincy.squad_selection_winner_id)?.side;
    if (!swSide) return IDLE_MS;
    const total = aPicked + bPicked;
    const turn  = (total % 2 === 0) ? swSide : (swSide === 'A' ? 'B' : 'A');
    if (!myMemberId) return FAST_MS;
    const myRow = s.squad.find(m => m.member_id === myMemberId);
    if (!myRow || !myRow.is_captain) return FAST_MS;
    return (turn === myRow.side) ? SLOW_MS : FAST_MS;
  }

  useEffect(() => {
    if (!matchId) return;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      if (document.hidden) {
        timerRef.current = setTimeout(tick, IDLE_MS);
        return;
      }
      await fetchOnce();
      if (stopped) return;
      timerRef.current = setTimeout(tick, computeInterval(stateRef.current));
    };

    fetchOnce().then(() => {
      if (stopped) return;
      timerRef.current = setTimeout(tick, computeInterval(stateRef.current));
    });

    const onVis = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [matchId]);

  return { state, error, refetch: fetchOnce, loading };
}
